import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileText, MapPin, Package } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Credentials = () => {
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCredentials();
  }, []);

  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/credentials`);
      setCredentials(response.data);
    } catch (error) {
      console.error('Error fetching credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="credentials-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-block border-l-4 border-orange-500 pl-4 mb-4">
              <p className="text-orange-500 font-oswald tracking-widest text-sm">OUR CREDENTIALS</p>
            </div>
            <h1 className="font-oswald font-bold text-5xl md:text-6xl mb-6">CERTIFIED EXCELLENCE</h1>
            <p className="text-xl text-slate-300 max-w-3xl">
              Trusted by leading industrial establishments across India
            </p>
          </motion.div>
        </div>
      </section>

      {/* Credentials Grid */}
      <section className="py-24 bg-slate-50" data-testid="credentials-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading credentials...</p>
            </div>
          ) : credentials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No credentials available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {credentials.map((cred, index) => (
                <motion.div
                  key={cred.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border-l-4 border-orange-500 shadow-sm hover:shadow-xl transition-all overflow-hidden group"
                  data-testid={`credential-card-${index}`}
                >
                  {/* Certificate Image */}
                  {cred.certificate_image ? (
                    <div className="h-48 overflow-hidden bg-slate-100">
                      <img 
                        src={cred.certificate_image} 
                        alt={cred.company_name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-slate-100 flex items-center justify-center">
                      <FileText className="w-12 h-12 text-slate-300" />
                    </div>
                  )}

                  <div className="p-6">
                    <h3 className="font-oswald font-bold text-xl text-slate-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {cred.company_name}
                    </h3>

                    <div className="space-y-2 text-sm text-slate-600 mb-4">
                      <div className="flex items-start">
                        <MapPin className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                        <span>{cred.location}</span>
                      </div>
                      {cred.executed_quantity && (
                        <div className="flex items-start">
                          <Package className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span><strong>Quantity:</strong> {cred.executed_quantity}</span>
                        </div>
                      )}
                    </div>

                    {cred.work_description && (
                      <div className="border-t border-slate-200 pt-4">
                        <p className="text-sm text-slate-700">{cred.work_description}</p>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Credentials;
